<template>
  <div>
    <template v-if="ready">
      <div class="d-flex justify-content-between align-items-center px-2">
        <h5 class="mb-2">{{ $t('simulacrum.select_form') }}</h5>
      </div>

      <div class="m-2">
        <v-select
          v-model="itemData.templates"
          :options="filteredTemplates"
          :reduce="(t) => t.id"
          label="label"
          :placeholder="$t('simulacrum.select_form_place_holder')"
          multiple
        />
      </div>

      <div class="mt-2" v-if="selectedTemplates.length">
        <div class="d-flex justify-content-between align-items-center">
          <h5>{{ $t('simulacrum.selected_form') }}</h5>
          <b-button variant="secondary" @click="onAnswerClick" :disabled="!mergedSnapshot.length || answering">
            {{ answering ? ($t('loading') || 'Loading...') : ($t('simulacrum.answer_form') || 'Start form') }}
          </b-button>
        </div>

        <!-- Preview questions -->
        <div class="mt-2">
          <h6 class="mt-1">{{ $tc('simulacrum.template.question', 2) || 'Questions' }}</h6>

          <div v-if="!mergedSnapshot.length" class="text-muted small">
            {{ $t('loading') || 'Loading...' }}
          </div>

          <ul v-else>
            <li v-for="(q, idx) in previewQuestions" :key="q.item_id || idx" class="mb-2">
              <p class="mb-1">
                <strong>{{ ($tc('simulacrum.template.question', 1) || 'Question') + '-' + (idx + 1) }}:</strong>
                <span class="text-muted">
                  [{{ q.section || $t('simulacrum.template.no_section') || 'No section' }}]
                </span>
                {{ q.name }}
              </p>
              <p class="mb-1">
                <strong>{{ $tc('custom_forms.types.title', 2) || 'Types' }}:</strong>
                {{ q.type }}
              </p>

              <template v-if="q.options && q.options.fieldTypeCfg && q.options.fieldTypeCfg.data && q.options.fieldTypeCfg.data.length">
                <p class="mb-1">
                  <strong>{{ $t('simulacrum.template.options') || 'Options' }}:</strong>
                </p>
                <ul class="mb-0">
                  <li v-for="(opt, oIdx) in q.options.fieldTypeCfg.data" :key="oIdx">
                    {{ opt.name }}
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div class="text-center" v-else>
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from '@vue/composition-api';
import axios from '@/libs/axios';
import vSelect from 'vue-select';
import { BButton, BSpinner } from 'bootstrap-vue';
import { useToast } from 'vue-toastification/composition';
import { shouldUpdateExistingRecordToInProgress } from '@/libs/risk/scheduledStartFormAccess';

export default {
  name: 'SimulacrumAssessmentStep',
  components: { vSelect, BButton, BSpinner },
  props: {
    propItemData: { type: Object, required: true },
    existingOperationalProcedureId: { type: Number, default: null },
    existingRecordLastStatusName: { type: String, default: null },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const itemData = ref({ ...props.propItemData, templates: [], reference_number: props.propItemData?.reference_number || `OP-${Date.now()}` });
    const ready = ref(false);
    const answering = ref(false);
    const templates = ref([]);
    const mergedSnapshot = ref([]);

    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get('/v1/operational_procedure_templates');
        templates.value = data.data || [];
      } catch (e) {
        console.error('Error fetching templates:', e);
        toast.error('Error al cargar los templates');
      }
    };

    const filteredTemplates = computed(() => {
      const tenantId = itemData.value.id_tenant;
      const list = templates.value || [];
      const byTenant = list.filter((t) => Number(t.id_tenant) === Number(tenantId));
      const source = byTenant.length ? byTenant : list; // fallback if none match tenant
      return source.map((t) => ({ id: t.id, label: t.name }));
    });
    const selectedTemplates = computed(() => {
      const selIds = new Set(itemData.value.templates || []);
      const byId = new Map((templates.value || []).map((t) => [t.id, t]));
      return [...selIds].map((id) => byId.get(id)).filter(Boolean);
    });

    const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
    const namespaceFieldIds = (tplId, fields) => (Array.isArray(fields) ? fields : []).map((f) => {
      const nf = deepClone(f);
      const oldId = nf.item_id;
      nf.item_id = `${tplId}:${oldId}`;
      if (Array.isArray(nf.show_if)) nf.show_if = nf.show_if.map((c) => (c && c.field ? { ...c, field: `${tplId}:${c.field}` } : c));
      return nf;
    });
    const mergeTemplatesToSnapshot = (templates) => {
      const out = [];
      (templates || []).forEach((tpl) => {
        const data = Array.isArray(tpl.data) ? tpl.data : [];
        namespaceFieldIds(tpl.id, data).forEach((f) => out.push(f));
      });
      return out;
    };

    const recomputeMergedSnapshot = () => { const sels = selectedTemplates.value; mergedSnapshot.value = sels.length ? mergeTemplatesToSnapshot(sels) : []; };

    // Enable button when a template is selected by recomputing snapshot
    watch(
      () => itemData.value.templates,
      (nv, ov) => { if (nv !== ov) recomputeMergedSnapshot(); }
    );

    const createOperationalProcedure = async () => {
      const payload = { reference_number: itemData.value.reference_number, id_unit: itemData.value.id, evaluation_date: new Date().toISOString()};
      const resp = await axios.post('/v1/operational_procedure', payload);
      return resp.data?.data?.id;
    };

    const resolveOperationalProcedureId = async () => {
      if (props.existingOperationalProcedureId != null && Number.isFinite(Number(props.existingOperationalProcedureId))) {
        return Number(props.existingOperationalProcedureId);
      }
      return createOperationalProcedure();
    };

    const isExistingScheduledFlow = () =>
      props.existingOperationalProcedureId != null && Number.isFinite(Number(props.existingOperationalProcedureId));

    const updateStatusToInProgress = async (procedureId) => {
      await axios.post(`/v1/operational_procedure/update_status/${procedureId}`, { status: "in_progress" });
    };

    const onAnswerClick = async () => {
      if (!selectedTemplates.value.length || !mergedSnapshot.value.length) return;
      answering.value = true;
      try {
        const procedureId = await resolveOperationalProcedureId();
        if (!procedureId) throw new Error("No se pudo crear el procedimiento");
        if (
          isExistingScheduledFlow() &&
          shouldUpdateExistingRecordToInProgress(props.existingRecordLastStatusName)
        ) {
          await updateStatusToInProgress(procedureId);
        }

        emit('open-simulacrum-modal', {
          id: procedureId,
          snapshot: mergedSnapshot.value,
          sources: (itemData.value.templates || []).slice(),
        });

        emit('assessment-complete');
      } catch (e) {
        console.error(e);
        const msg = e?.response?.data?.message || "No se pudo crear el procedimiento";
        toast.error(msg);
      } finally {
        answering.value = false;
      }
    };

    const previewQuestions = computed(() => mergedSnapshot.value);

    onMounted(async () => { await fetchTemplates(); ready.value = true; });

    return { ready, answering, itemData, filteredTemplates, selectedTemplates, mergedSnapshot, previewQuestions, onAnswerClick };
  },
};
</script>


